import React from 'react';
import { observer } from 'mobx-react';
import {
    Theme,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogActions,
    DialogContent,
} from '@mui/material';
import { createStyles, withStyles, WithStyles } from '@mui/styles';

import { WithTranslation, withTranslation } from 'react-i18next';
import TimerIcon from '@mui/icons-material/TimerOutlined';
import { ModalProps } from '@mui/material/Modal';

type ModalWithoutChildrenProps = Omit<ModalProps, 'children'>;

export enum CloseReason {
    BackdropClick,
    ConfirmButtonClick,
    Countdown,
}

interface IdleCounterModalProps
    extends WithStyles<typeof styles>,
        WithTranslation {
    modalProps: ModalWithoutChildrenProps;
    onClose(reason: CloseReason): void;

    idleWaitTime: number;
}

const styles = (theme: Theme) =>
    createStyles({
        root: {
            padding: '50px',
        },

        header: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        },
        body: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            height: '100%',
            padding: '60px 0',
        },
        timerIcon: {
            fontSize: '5rem',
            fill: theme.palette.primary.main,
        },
        timer: {
            marginTop: 5,
            fontWeight: 'normal',
        },

        footer: {
            justifyContent: 'center',
        },
        button: {
            minWidth: '250px',
            height: '80px',
        },
    });

@observer
class IdleCounterModal extends React.Component<IdleCounterModalProps, any> {
    timerId?: any;

    constructor(props: IdleCounterModalProps) {
        super(props);

        this.state = {
            idleWaitTime: props.idleWaitTime,
        };
    }

    componentDidMount() {
        this.startCountdown();
    }

    componentWillUnmount() {
        if (this.timerId) {
            clearTimeout(this.timerId);
        }
    }

    render() {
        const { onClose, modalProps, classes, t } = this.props;
        const { idleWaitTime } = this.state;

        return (
            <Dialog
                classes={{ paper: classes.root }}
                onClose={() => onClose(CloseReason.BackdropClick)}
                {...modalProps}
            >
                <DialogTitle variant='h2' style={{ fontWeight: 'normal' }}>
                    {t('Are you still here?')}
                </DialogTitle>
                <DialogContent className={classes.body}>
                    <TimerIcon className={classes.timerIcon} />
                    <Typography variant='h4' className={classes.timer}>
                        {idleWaitTime} {t('seconds')}
                    </Typography>
                </DialogContent>
                <DialogActions className={classes.footer}>
                    <Button
                        className={classes.button}
                        variant='primary'
                        onClick={(e) => {
                            onClose(CloseReason.ConfirmButtonClick);
                        }}
                    >
                        <Typography variant='button'>{t('Yes')}</Typography>
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    startCountdown() {
        const { onClose } = this.props;

        this.timerId = setTimeout(() => {
            const { idleWaitTime } = this.state;

            if (idleWaitTime === 0) {
                onClose(CloseReason.Countdown);
                return;
            }

            this.setState({ idleWaitTime: idleWaitTime - 1 });
            this.startCountdown();
        }, 1 * 1000);
    }
}

export default withTranslation()(withStyles(styles)(IdleCounterModal));
